type CampaignInfo = {
  campaignId: string;
  entryId: string;
  href: string;
  src: string;
  deadlineDate: string;
};

const campaignTrackEvent = (
  eventName: string,
  userId: string,
  campaignId: string,
) => {
  const firebaseClient = (window as any).firebaseClient || undefined;

  if (!firebaseClient) return;

  try {
    firebaseClient.trackEvent(userId, eventName, { campaign_id: campaignId });
  } catch {
    // NOTE: Ignore errors that occur in trackEvent
  }
};

class CampaignEntryList {
  userId: string | undefined;
  campaignId: string | undefined;

  constructor() {
    const cards = document.querySelectorAll<HTMLElement>('.campaign-info');
    const container = document.querySelector<HTMLElement>(
      '#campaigns-field-container',
    )!;
    this.userId = container.dataset.userId;
    this.campaignId = undefined;

    cards.forEach((card) => {
      const bannerLink = card.querySelector<HTMLElement>('.campaign-image a');
      bannerLink?.addEventListener('click', (e) =>
        this.handleClick(e, 'click_banner_campaign_entry'),
      );

      const titleLink = card.querySelector<HTMLElement>('.campaign-title a');
      titleLink?.addEventListener('click', (e) =>
        this.handleClick(e, 'click_title_campaign_entry'),
      );
    });
  }

  handleClick(e: Event, eventName: string) {
    const cardInfo = (e.currentTarget as HTMLElement).closest<HTMLElement>(
      '.campaign-info',
    );
    this.campaignId = cardInfo?.dataset.id;
    campaignTrackEvent(eventName, this.userId!, this.campaignId!);
  }
}

class CampaignEntryModal {
  completedModal: CampaignEntryCompletedModal;
  campaignId: string | null;
  entryId: string | null;
  trigger: HTMLElement | null;
  userId: string | undefined;
  learnerId: string | undefined;
  elements: {
    modal: HTMLElement;
    $modal: JQuery<HTMLElement>;
    checkbox: HTMLInputElement;
    entryButton: HTMLButtonElement;
    cancelButton: HTMLButtonElement;
    contentLink: HTMLAnchorElement;
    img: HTMLImageElement;
    detailLink: HTMLAnchorElement;
    deadlineDate: HTMLElement;
  };

  constructor(completedModal: CampaignEntryCompletedModal) {
    const modal = document.querySelector<HTMLElement>('#campaignEntryModal')!;

    this.completedModal = completedModal;
    this.campaignId = null;
    this.entryId = null;
    this.trigger = null;

    this.userId = modal.dataset.userId;
    this.learnerId = modal.dataset.learnerId;
    this.elements = {
      modal: modal,
      $modal: $(modal),
      checkbox: modal.querySelector<HTMLInputElement>('.entry-check')!,
      entryButton: modal.querySelector<HTMLButtonElement>('.entry-button')!,
      cancelButton: modal.querySelector<HTMLButtonElement>('.cancel-button')!,
      contentLink: modal.querySelector<HTMLAnchorElement>('.campaign-content')!,
      img: modal.querySelector<HTMLImageElement>('.campaign-content img')!,
      detailLink: modal.querySelector<HTMLAnchorElement>('.campaign-detail a')!,
      deadlineDate: modal.querySelector<HTMLElement>('.entry-deadline-date')!,
    };

    this.elements.$modal
      .on('show.bs.modal', (e: any) => {
        this.trigger = e.relatedTarget;
        this.show();
      })
      .on('hidden.bs.modal', () => {
        this.clear();
        setTimeout(() => {
          this.trigger?.blur();
          this.trigger = null;
        });
      });

    this.elements.img.addEventListener('click', () => {
      campaignTrackEvent(
        'click_entry_modal_banner_campaign_entry',
        this.userId!,
        this.campaignId!,
      );
    });

    this.elements.detailLink.addEventListener('click', () => {
      campaignTrackEvent(
        'click_detail_campaign_entry',
        this.userId!,
        this.campaignId!,
      );
    });

    this.elements.checkbox.addEventListener('click', () => {
      if (this.elements.checkbox.checked) {
        this.confirmed();
      } else {
        this.unconfirmed();
      }
    });

    this.elements.entryButton.addEventListener('click', () => {
      if (!this.elements.checkbox.checked) return;

      this.suspendButton();
      this.entry()
        .done((_data) => {
          campaignTrackEvent(
            'enter_campaign_entry',
            this.userId!,
            this.campaignId!,
          );
          window.krt?.('send', 'campaign-entry', {
            campaign_id: this.campaignId,
            status: 1,
          });
          this.completedModal.showEntrySuccess();
        })
        .fail((xhr) => {
          if (xhr.responseJSON.error === 'NotFoundError') {
            this.completedModal.showEntryFailedNotFound();
          } else {
            this.completedModal.showEntryFailedServerError();
          }
        })
        .always(() => {
          this.elements.$modal.modal('hide');
        });
    });

    this.elements.cancelButton.addEventListener('click', () => {
      this.suspendButton();
      this.cancel()
        .done((_data) => {
          campaignTrackEvent(
            'cancel_campaign_entry',
            this.userId!,
            this.campaignId!,
          );
          window.krt?.('send', 'campaign-entry', {
            campaign_id: this.campaignId,
            status: 2,
          });
          this.completedModal.showCancelSuccess();
        })
        .fail((xhr) => {
          if (xhr.responseJSON.error === 'NotFoundError') {
            this.completedModal.showCancelFailedNotFound();
          } else {
            this.completedModal.showCancelServerError();
          }
        })
        .always(() => {
          this.elements.$modal.modal('hide');
        });
    });
  }

  entry() {
    return $.ajax({
      url: `/my/learners/${this.learnerId}/campaigns/${this.campaignId}/entry`,
      dataType: 'json',
      method: 'POST',
    });
  }

  cancel() {
    return $.ajax({
      url: `/my/learners/${this.learnerId}/campaigns/${this.campaignId}/cancel`,
      dataType: 'json',
      method: 'DELETE',
      data: { entry_id: this.entryId },
    });
  }

  setModal(
    type: string | null = null,
    href: string = '#',
    src: string = '',
    deadlineDate: string = '',
  ) {
    if (type) this.elements.modal.classList.add(type);
    this.elements.contentLink.href = href;
    this.elements.detailLink.href = href;
    this.elements.img.src = src;
    this.elements.deadlineDate.innerText = deadlineDate;
  }

  show() {
    if (!this.trigger) return;

    const entryStatus = this.trigger.dataset['entryStatus'];

    let type: string | null = null;
    if (entryStatus === 'open-for-entries') {
      type = 'modal-type-entry';
    } else if (entryStatus === 'entered') {
      type = 'modal-type-cancel';
    }

    const info = this.buildCampaignInfo(
      this.trigger.closest<HTMLElement>('.campaign-info')!,
    );
    this.campaignId = info.campaignId;
    this.entryId = info.entryId;
    this.setModal(type, info.href, info.src, info.deadlineDate);
    if (type === 'modal-type-entry') {
      campaignTrackEvent(
        'show_entry_modal_campaign_entry',
        this.userId!,
        this.campaignId!,
      );
    }
  }

  buildCampaignInfo(card: HTMLElement): CampaignInfo {
    return {
      campaignId: card.dataset['id']!,
      entryId: card.dataset['entryId']!,
      href: card.querySelector<HTMLAnchorElement>('.campaign-image a')!.href,
      src: card.querySelector<HTMLImageElement>('.campaign-image img')!.src,
      deadlineDate: card.querySelector<HTMLElement>('.campaign-deadline-date')!
        .innerText,
    };
  }

  clear() {
    this.campaignId = null;
    this.entryId = null;
    this.elements.checkbox.checked = false;
    this.unconfirmed();
    this.elements.modal.classList.remove(
      'modal-type-entry',
      'modal-type-cancel',
    );
    this.elements.cancelButton.classList.remove('disabled');
    this.elements.cancelButton.classList.add('btn-outline-primary');
    this.setModal();
  }

  confirmed() {
    this.elements.entryButton.classList.remove('disabled');
    this.elements.entryButton.classList.add('btn-primary');
  }

  unconfirmed() {
    this.elements.entryButton.classList.remove('btn-primary');
    this.elements.entryButton.classList.add('disabled');
  }

  suspendButton() {
    this.elements.entryButton.classList.remove('btn-primary');
    this.elements.entryButton.classList.add('disabled');
    this.elements.cancelButton.classList.remove('btn-outline-primary');
    this.elements.cancelButton.classList.add('disabled');
  }
}

class CampaignEntryCompletedModal {
  element: HTMLElement;
  $modal: JQuery<HTMLElement>;

  constructor() {
    this.element = document.querySelector<HTMLElement>(
      '#campaignEntryCompletedModal',
    )!;
    this.$modal = $(this.element);

    this.$modal.on('hidden.bs.modal', () => {
      this.element.classList.remove(
        'modal-type-entry-success',
        'modal-type-entry-failed-not-found',
        'modal-type-entry-failed-server-error',
        'modal-type-cancel-success',
        'modal-type-cancel-failed-not-found',
        'modal-type-cancel-failed-server-error',
      );
      // TODO: https://github.com/quipper/aya-issues/issues/70236
      location.reload();
    });
  }

  showEntrySuccess() {
    this.element.classList.add('modal-type-entry-success');
    this.$modal.modal('show');
  }

  showEntryFailedNotFound() {
    this.element.classList.add('modal-type-entry-failed-not-found');
    this.$modal.modal('show');
  }

  showEntryFailedServerError() {
    this.element.classList.add('modal-type-entry-failed-server-error');
    this.$modal.modal('show');
  }

  showCancelSuccess() {
    this.element.classList.add('modal-type-cancel-success');
    this.$modal.modal('show');
  }

  showCancelFailedNotFound() {
    this.element.classList.add('modal-type-cancel-failed-not-found');
    this.$modal.modal('show');
  }

  showCancelServerError() {
    this.element.classList.add('modal-type-cancel-failed-server-error');
    this.$modal.modal('show');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector<HTMLElement>('#campaigns-field')) return;

  const emailNewsletterContainer = document.querySelector<HTMLElement>(
    '.email-newsletter-container',
  )!;
  const enableReceiveLatestInfo = document.querySelector<HTMLButtonElement>(
    '#email-newsletter-activation-setting-submit',
  )!;
  const emailNewsletterDanger = document.querySelector<HTMLElement>(
    '#email-newsletter-danger',
  )!;

  const enableReceiveLatestInfoHandler = () => {
    enableReceiveLatestInfo.classList.remove('btn-primary');
    enableReceiveLatestInfo.setAttribute('disabled', 'disabled');

    $.ajax({
      url: '/my/accounts/enable_receive_latest_info',
      dataType: 'json',
      method: 'PATCH',
    })
      .done((_data) => {
        emailNewsletterContainer.classList.remove(
          'receive-latest-info-receive',
        );
        window.krt?.('send', 'aya_click_set-account-receive-latest-info');
      })
      .fail(() => {
        emailNewsletterDanger.textContent = '更新に失敗しました';
        emailNewsletterDanger.classList.remove('hidden');
      });
  };

  enableReceiveLatestInfo.addEventListener(
    'click',
    enableReceiveLatestInfoHandler,
  );

  if (!document.querySelector<HTMLElement>('#campaigns-field-container'))
    return;

  new CampaignEntryList();
  new CampaignEntryModal(new CampaignEntryCompletedModal());
});
